import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDeliverables } from "@/hooks/useLive";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CATEGORIES, STATUS_MAP } from "@/data/categories";
import type { Category, Deliverable, Status, TaskActivity } from "@/lib/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { CreateDeliverableModal } from "@/components/forms/CreateModals";
import { consumeTaskFilter } from "@/lib/taskFilter";
import { useAuth } from "@/hooks/useAuth";
import { ROSTER } from "@/data/roster";
import { cn, daysUntil, parseLaunchDate, primaryOwner } from "@/lib/utils";
import { CalendarClock, CheckCircle2, ChevronDown, ChevronLeft, CircleDot, Flag, Plus, Search, Users, ExternalLink, AlertCircle, Ban, X, Send, MessageSquare, Pencil, Trash2 } from "lucide-react";

type DueBucket = "all" | "past_due" | "next_14" | "next_30" | "later" | "no_date";
const DUE_OPTIONS: { value: DueBucket; label: string }[] = [
  { value: "all", label: "All dates" },
  { value: "past_due", label: "Past due" },
  { value: "next_14", label: "Next 14 days" },
  { value: "next_30", label: "Next 30 days" },
  { value: "later", label: "Later" },
  { value: "no_date", label: "No date" },
];

function dueSortKey(due: string, now: Date): number {
  const p = parseLaunchDate(due);
  if (!p) return 99999;
  return daysUntil(p, now);
}

function bucketFor(due: string, now: Date): DueBucket {
  const p = parseLaunchDate(due);
  if (!p) return "no_date";
  const d = daysUntil(p, now);
  if (d < 0) return "past_due";
  if (d <= 14) return "next_14";
  if (d <= 30) return "next_30";
  return "later";
}

function StatCard({
  label,
  value,
  Icon,
  iconClass,
  iconBg,
  valueClass,
  footer,
}: {
  label: string;
  value: number;
  Icon: typeof Flag;
  iconClass: string;
  iconBg: string;
  valueClass?: string;
  footer?: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-card">
      <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", iconBg)}>
        <Icon size={18} className={iconClass} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-micro font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className={cn("text-h2 font-bold tabular leading-none text-foreground", valueClass)}>
          {value}
        </div>
        {footer && <div className="mt-0.5 text-micro text-muted-foreground">{footer}</div>}
      </div>
    </div>
  );
}

function TaskRow({ d, now }: { d: Deliverable; now: Date }) {
  const qc = useQueryClient();
  const [localStatus, setLocalStatus] = useState<Status>(d.status);
  const [saving, setSaving] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => { setLocalStatus(d.status); }, [d.status]);

  const parsed = parseLaunchDate(d.due);
  const overdue = parsed ? daysUntil(parsed, now) < 0 && localStatus !== "complete" : false;

  const handleStatus = useCallback(async (next: Status) => {
    const prev = localStatus;
    setLocalStatus(next);
    setSaving(true);
    try {
      await fetch(`/api/deliverables/${d.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
        credentials: "include",
      });
      qc.invalidateQueries({ queryKey: ["deliverables"] });
    } catch { setLocalStatus(prev); }
    finally { setSaving(false); }
  }, [d.id, localStatus, qc]);

  const hasNotes = Boolean(d.notes?.trim());

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setDetailsOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailsOpen(true); } }}
        className={cn(
          "group flex cursor-pointer flex-col gap-2 rounded-md border border-transparent px-3 py-2.5 md:flex-row md:items-center md:gap-3",
          d.flag ? "bg-destructive/5 hover:bg-destructive/10" : "bg-muted/20 hover:bg-muted/40"
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            {overdue && <Flag size={12} className="mt-1 shrink-0 text-destructive" />}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-body font-medium text-foreground">{d.name}</span>
                {hasNotes && (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-micro text-muted-foreground">notes</span>
                )}
                {d.link && (
                  <a
                    href={d.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-1.5 py-0.5 text-micro text-muted-foreground hover:border-primary/40 hover:text-primary"
                    title={d.link}
                  >
                    <ExternalLink size={10} /> open
                  </a>
                )}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-micro text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Avatar name={primaryOwner(d.owner)} size="xs" /> {d.owner || "—"}
                </span>
                {d.phase && <span>· {d.phase}</span>}
                {d.flag && <span className="text-destructive">· {d.flag}</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <span className={cn("text-micro tabular", overdue ? "font-semibold text-destructive" : "text-muted-foreground")}>
            {d.due || "—"}
          </span>
          <select
            value={localStatus}
            onChange={(e) => handleStatus(e.target.value as Status)}
            disabled={saving}
            className={cn("rounded-md border border-border bg-card px-2 py-1 text-caption focus:outline-none focus:ring-1 focus:ring-primary", saving && "opacity-50 cursor-wait")}
          >
            {(Object.keys(STATUS_MAP) as Status[]).map((k) => (
              <option key={k} value={k}>{STATUS_MAP[k].label}</option>
            ))}
          </select>
        </div>
      </div>
      {detailsOpen && (
        <TaskDetailsModal d={d} onClose={() => setDetailsOpen(false)} />
      )}
    </>
  );
}

/** Parse a free-form dependencies string into ids + unmatched text. */
function parseDependencies(raw: string | undefined, allTasks: Deliverable[]): { ids: string[]; unknown: string[] } {
  if (!raw?.trim()) return { ids: [], unknown: [] };
  const parts = raw.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
  const ids: string[] = [];
  const unknown: string[] = [];
  const known = new Set(allTasks.map((t) => String(t.id)));
  for (const p of parts) {
    if (known.has(p)) ids.push(p);
    else unknown.push(p);
  }
  return { ids, unknown };
}

function TaskDetailsModal({ d: initial, onClose }: { d: Deliverable; onClose: () => void }) {
  // Modal stack — clicking a dependency pushes a new task on top; back pops.
  const [stack, setStack] = useState<Deliverable[]>([initial]);
  const top = stack[stack.length - 1];

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (stack.length > 1) setStack((s) => s.slice(0, -1));
        else onClose();
      }
    };
    document.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", h); document.body.style.overflow = ""; };
  }, [stack.length, onClose]);

  const pushTask = (t: Deliverable) => setStack((s) => [...s, t]);
  const popTask = () => setStack((s) => s.slice(0, -1));

  return createPortal(
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center bg-black/65 p-4 backdrop-blur-lg"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <TaskDetailsBody
          key={top.id}
          d={top}
          onClose={onClose}
          canBack={stack.length > 1}
          onBack={popTask}
          onNavigate={pushTask}
        />
      </div>
    </div>,
    document.body
  );
}

function TaskDetailsBody({ d, onClose, canBack, onBack, onNavigate }: {
  d: Deliverable;
  onClose: () => void;
  canBack: boolean;
  onBack: () => void;
  onNavigate: (t: Deliverable) => void;
}) {
  const qc = useQueryClient();
  const allTasks = useDeliverables();

  // Live copy of the task — falls back to d if absent so we always render.
  const live = useMemo(() => allTasks.find((t) => String(t.id) === String(d.id)) ?? d, [allTasks, d]);

  // Editable fields
  const [name, setName] = useState(live.name ?? "");
  const [owner, setOwner] = useState(live.owner ?? "");
  const [notes, setNotes] = useState(live.notes ?? "");
  const [link, setLink] = useState(live.link ?? "");
  const [due, setDue] = useState(live.due ?? "");
  const [phase, setPhase] = useState(live.phase ?? "");
  const [status, setStatusVal] = useState<Status>(live.status);
  const [writerSupport, setWriterSupport] = useState(live.writerSupport ?? "");
  const [approvals, setApprovals] = useState(live.approvals ?? "");
  const [flag, setFlag] = useState(live.flag ?? "");
  const [category, setCategory] = useState<Category>(live.category);
  const [depIds, setDepIds] = useState<string[]>(() => parseDependencies(live.dependencies, allTasks).ids);
  const [editingTitle, setEditingTitle] = useState(false);

  useEffect(() => { setName(live.name ?? ""); }, [live.id, live.name]);
  useEffect(() => { setOwner(live.owner ?? ""); }, [live.id, live.owner]);
  useEffect(() => { setNotes(live.notes ?? ""); }, [live.id, live.notes]);
  useEffect(() => { setLink(live.link ?? ""); }, [live.id, live.link]);
  useEffect(() => { setDue(live.due ?? ""); }, [live.id, live.due]);
  useEffect(() => { setPhase(live.phase ?? ""); }, [live.id, live.phase]);
  useEffect(() => { setStatusVal(live.status); }, [live.id, live.status]);
  useEffect(() => { setWriterSupport(live.writerSupport ?? ""); }, [live.id, live.writerSupport]);
  useEffect(() => { setApprovals(live.approvals ?? ""); }, [live.id, live.approvals]);
  useEffect(() => { setFlag(live.flag ?? ""); }, [live.id, live.flag]);
  useEffect(() => { setCategory(live.category); }, [live.id, live.category]);
  useEffect(() => { setDepIds(parseDependencies(live.dependencies, allTasks).ids); }, [live.id, live.dependencies, allTasks]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { isAdmin, user } = useAuth();
  const userFirstName = (user?.name ?? user?.email ?? "").split(/[\s@]/)[0].toLowerCase();
  const ownerFirstName = String(live.owner ?? "").split(/[,/\s]/)[0].trim().toLowerCase();
  const canDelete = isAdmin || (!!userFirstName && userFirstName === ownerFirstName);

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    try {
      const res = await fetch(`/api/deliverables/${d.id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error ?? `HTTP ${res.status}`); }
      qc.invalidateQueries({ queryKey: ["deliverables"] });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const depString = depIds.join(", ");
  const origDeps = parseDependencies(live.dependencies, allTasks).ids.join(", ");
  const dirty =
    name.trim() !== (live.name ?? "").trim() ||
    owner !== (live.owner ?? "") ||
    notes !== (live.notes ?? "") ||
    link !== (live.link ?? "") ||
    due !== (live.due ?? "") ||
    phase !== (live.phase ?? "") ||
    status !== live.status ||
    writerSupport !== (live.writerSupport ?? "") ||
    approvals !== (live.approvals ?? "") ||
    flag !== (live.flag ?? "") ||
    category !== live.category ||
    depString !== origDeps;

  const save = async () => {
    if (!dirty) { onClose(); return; }
    if (!name.trim()) { setError("Name cannot be empty."); return; }
    setSaving(true);
    setError(null);
    const patch: Record<string, string> = {};
    if (name.trim() !== (live.name ?? "").trim()) patch.name = name.trim();
    if (owner !== (live.owner ?? "")) patch.owner = owner;
    if (notes !== (live.notes ?? "")) patch.notes = notes;
    if (link !== (live.link ?? "")) patch.link = link;
    if (due !== (live.due ?? "")) patch.due = due;
    if (phase !== (live.phase ?? "")) patch.phase = phase;
    if (status !== live.status) patch.status = status;
    if (writerSupport !== (live.writerSupport ?? "")) patch.writerSupport = writerSupport;
    if (approvals !== (live.approvals ?? "")) patch.approvals = approvals;
    if (flag !== (live.flag ?? "")) patch.flag = flag;
    if (category !== live.category) patch.category = category;
    if (depString !== origDeps) patch.dependencies = depString;
    try {
      const res = await fetch(`/api/deliverables/${d.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      qc.invalidateQueries({ queryKey: ["deliverables"] });
      qc.invalidateQueries({ queryKey: ["task-activity", String(d.id)] });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const dependencyTasks = depIds.map((id) => allTasks.find((t) => String(t.id) === id)).filter(Boolean) as Deliverable[];
  const blockingTasks = useMemo(() => allTasks.filter((t) => parseDependencies(t.dependencies, allTasks).ids.includes(String(d.id))), [allTasks, d.id]);

  return (
    <>
      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-card/95 px-5 py-4 backdrop-blur">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {canBack && (
            <button onClick={onBack} className="mt-0.5 shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title="Back">
              <ChevronLeft size={16} />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-micro uppercase tracking-wider text-muted-foreground">
              Task #{d.id} · {CATEGORIES[category]?.label ?? category}
              {d.source && d.source !== "manual" && (
                <span
                  className="ml-2 inline-flex items-center rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-muted-foreground"
                  title={`Created via ${d.source}`}
                >
                  via {d.source === "ask_ai" ? "Felix AI" : d.source === "proposal" ? "Proposal" : d.source === "orin" ? "Orin" : d.source}
                </span>
              )}
            </div>
            {editingTitle ? (
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setEditingTitle(false)}
                onKeyDown={(e) => { if (e.key === "Enter") setEditingTitle(false); if (e.key === "Escape") { setName(live.name ?? ""); setEditingTitle(false); } }}
                className="mt-1 w-full rounded-md border border-primary/30 bg-background px-2 py-1 text-h4 font-semibold text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
              />
            ) : (
              <button
                onClick={() => setEditingTitle(true)}
                className="group mt-1 flex w-full items-center gap-2 text-left text-h4 font-semibold text-foreground hover:text-primary"
              >
                <span className="truncate">{name || "Untitled task"}</span>
                <Pencil size={12} className="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
              </button>
            )}
          </div>
        </div>
        <button onClick={onClose} className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" title="Close">
          <X size={16} />
        </button>
      </div>

      <div className="space-y-5 px-5 py-5">
        <div className="grid grid-cols-2 gap-3 text-caption">
          <OwnerField value={owner} onChange={setOwner} />
          <EditableField label="Due" value={due} onChange={setDue} placeholder="May 4, Late May, …" />
          <EditableSelect
            label="Phase"
            value={phase}
            onChange={setPhase}
            options={[
              { value: "", label: "—" },
              { value: "Alpha+", label: "Alpha+" },
              { value: "Closed Beta 1", label: "Closed Beta 1" },
              { value: "Closed Beta 2", label: "Closed Beta 2" },
              { value: "GA", label: "GA" },
              { value: "Post-GA", label: "Post-GA" },
            ]}
          />
          <EditableSelect
            label="Status"
            value={status}
            onChange={(v) => setStatusVal(v as Status)}
            options={(Object.keys(STATUS_MAP) as Status[]).map((k) => ({ value: k, label: STATUS_MAP[k].label }))}
          />
          <EditableSelect
            label="Category"
            value={category}
            onChange={(v) => setCategory(v as Category)}
            options={(Object.keys(CATEGORIES) as Category[]).map((k) => ({ value: k, label: CATEGORIES[k].label }))}
          />
          <EditableField label="Writer support" value={writerSupport} onChange={setWriterSupport} placeholder="Optional" />
          <EditableField label="Approvals" value={approvals} onChange={setApprovals} placeholder="Optional" />
          <EditableField label="Flag" value={flag} onChange={setFlag} placeholder="Leave empty for none" valueClassName={flag ? "text-destructive" : undefined} />
        </div>

        <DependencyEditor
          depIds={depIds}
          setDepIds={setDepIds}
          allTasks={allTasks}
          currentId={String(d.id)}
          onNavigate={onNavigate}
          tasks={dependencyTasks}
        />

        {blockingTasks.length > 0 && (
          <div>
            <div className="mb-1.5 text-micro uppercase tracking-wider text-muted-foreground">Blocks ({blockingTasks.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {blockingTasks.map((t) => (
                <DepChip key={t.id} task={t} onClick={() => onNavigate(t)} />
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="mb-1 block text-caption font-semibold text-muted-foreground">Link</label>
          <input
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://..."
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-body text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
          />
        </div>

        <div>
          <label className="mb-1 block text-caption font-semibold text-muted-foreground">Details / notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            placeholder="Context, scope, acceptance, open questions…"
            className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-body leading-relaxed text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
          />
        </div>

        {error && <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-caption text-destructive">{error}</div>}

        <div className="flex items-center justify-between pt-1">
          {canDelete ? (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-caption font-medium transition-colors disabled:opacity-50",
                confirmDelete
                  ? "bg-destructive text-white hover:bg-destructive/90"
                  : "border border-border text-destructive hover:bg-destructive/5"
              )}
            >
              <Trash2 size={13} />
              {deleting ? "Deleting…" : confirmDelete ? "Confirm delete" : "Delete task"}
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button
              onClick={() => { setConfirmDelete(false); onClose(); }}
              className="rounded-md border border-border bg-card px-4 py-2 text-caption text-foreground hover:bg-muted"
            >
              Close
            </button>
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="rounded-md bg-primary px-4 py-2 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        <ActivityFeed taskId={String(d.id)} />
      </div>
    </>
  );
}

function DepChip({ task, onClick }: { task: Deliverable; onClick: () => void }) {
  const STATUS_DOT: Record<Status, string> = {
    complete: "bg-emerald-500",
    in_progress: "bg-blue-500",
    not_started: "bg-muted-foreground/40",
    blocked: "bg-destructive",
    at_risk: "bg-amber-500",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2 py-1 text-caption text-foreground hover:border-primary/40 hover:bg-muted"
      title={`#${task.id} · ${STATUS_MAP[task.status]?.label ?? task.status}`}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", STATUS_DOT[task.status])} />
      <span className="truncate">#{task.id} {task.name}</span>
    </button>
  );
}

function DependencyEditor({ depIds, setDepIds, allTasks, currentId, onNavigate, tasks }: {
  depIds: string[];
  setDepIds: (ids: string[]) => void;
  allTasks: Deliverable[];
  currentId: string;
  onNavigate: (t: Deliverable) => void;
  tasks: Deliverable[];
}) {
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!picking) return;
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setPicking(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [picking]);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTasks
      .filter((t) => String(t.id) !== currentId && !depIds.includes(String(t.id)))
      .filter((t) => !q || `${t.id} ${t.name} ${t.owner}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [allTasks, depIds, currentId, query]);

  const add = (id: string) => { setDepIds([...depIds, id]); setQuery(""); };
  const remove = (id: string) => setDepIds(depIds.filter((x) => x !== id));

  return (
    <div ref={wrapRef}>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-micro uppercase tracking-wider text-muted-foreground">Dependencies</span>
        <button
          type="button"
          onClick={() => setPicking((v) => !v)}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-micro text-muted-foreground hover:border-primary/40 hover:text-primary"
        >
          <Plus size={11} /> Add
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tasks.length === 0 && !picking && (
          <span className="text-caption text-muted-foreground">None — click <strong>Add</strong> to link a task this depends on.</span>
        )}
        {tasks.map((t) => (
          <span key={t.id} className="inline-flex items-center gap-1">
            <DepChip task={t} onClick={() => onNavigate(t)} />
            <button
              type="button"
              onClick={() => remove(String(t.id))}
              className="rounded-full p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              title="Remove dependency"
            >
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
      {picking && (
        <div className="relative mt-2">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tasks by id or name…"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-body text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
          />
          <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-card shadow-card">
            {candidates.length === 0 ? (
              <div className="px-3 py-2 text-caption text-muted-foreground">No matches.</div>
            ) : candidates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => add(String(t.id))}
                className="block w-full px-3 py-2 text-left text-caption hover:bg-muted"
              >
                <span className="font-medium">#{t.id}</span> · {t.name}
                <span className="ml-2 text-micro text-muted-foreground">{t.owner || "—"}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OwnerField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return ROSTER.slice(0, 6);
    return ROSTER.filter((p) => p.name.toLowerCase().includes(q) || p.firstName.toLowerCase().includes(q) || p.aliases.some((a) => a.includes(q))).slice(0, 6);
  }, [value]);

  return (
    <div ref={wrapRef}>
      <div className="text-micro uppercase tracking-wider text-muted-foreground">Owner</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder="Type a name or 'Ryan / Tom'"
        className="mt-0.5 w-full rounded-md border border-border bg-background px-2 py-1 text-caption text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
      />
      {open && matches.length > 0 && (
        <div className="relative">
          <div className="absolute left-0 right-0 z-20 mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-card shadow-card">
            {matches.map((p) => (
              <button
                key={p.email}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onChange(p.firstName); setOpen(false); }}
                className="block w-full px-3 py-1.5 text-left text-caption hover:bg-muted"
              >
                <span className="font-medium">{p.name}</span>
                {p.role && <span className="ml-2 text-micro text-muted-foreground">{p.role}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityFeed({ taskId }: { taskId: string }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: activity = [], isLoading } = useQuery<TaskActivity[]>({
    queryKey: ["task-activity", taskId],
    queryFn: async () => {
      const res = await fetch(`/api/deliverables/${taskId}/activity`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    staleTime: 5_000,
  });

  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  const post = async () => {
    const text = body.trim();
    if (!text) return;
    setPosting(true);
    try {
      const optimistic: TaskActivity = {
        id: `tmp-${Date.now()}`,
        taskId,
        authorEmail: user?.email,
        authorName: user?.name ?? user?.email ?? "You",
        kind: "comment",
        body: text,
        createdAt: new Date().toISOString(),
      };
      qc.setQueryData<TaskActivity[]>(["task-activity", taskId], (prev) => [optimistic, ...(prev ?? [])]);
      setBody("");
      const res = await fetch(`/api/deliverables/${taskId}/activity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: text }),
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      qc.invalidateQueries({ queryKey: ["task-activity", taskId] });
    } catch {
      qc.invalidateQueries({ queryKey: ["task-activity", taskId] });
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="border-t border-border pt-5">
      <div className="mb-3 flex items-center gap-2 text-micro uppercase tracking-wider text-muted-foreground">
        <MessageSquare size={11} /> Activity
      </div>

      <div className="mb-4 flex items-start gap-2">
        <Avatar name={user?.name ?? user?.email ?? "You"} size="sm" />
        <div className="flex-1">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") post(); }}
            rows={2}
            placeholder="Leave an update… (⌘+Enter to post)"
            className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-caption text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
          />
          <div className="mt-1 flex justify-end">
            <button
              onClick={post}
              disabled={posting || !body.trim()}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Send size={11} /> {posting ? "Posting…" : "Post"}
            </button>
          </div>
        </div>
      </div>

      <ul className="space-y-3">
        {isLoading && activity.length === 0 && (
          <li className="text-caption text-muted-foreground">Loading activity…</li>
        )}
        {!isLoading && activity.length === 0 && (
          <li className="text-caption text-muted-foreground">No activity yet. Status changes and comments will show up here.</li>
        )}
        {activity.map((a) => (
          <li key={a.id} className="flex items-start gap-2">
            <Avatar name={a.authorName || a.authorEmail || "Felix"} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-micro text-muted-foreground">
                <span className="font-medium text-foreground">{a.authorName || a.authorEmail || "Felix"}</span>
                <span>· {relTime(a.createdAt)}</span>
                {a.kind === "system" && <span className="rounded-full bg-muted px-1.5 py-0.5 text-micro uppercase tracking-wider">system</span>}
              </div>
              <div className={cn("mt-0.5 whitespace-pre-wrap break-words text-caption", a.kind === "system" ? "text-muted-foreground" : "text-foreground")}>
                {a.body}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Date.now() - t;
  const s = Math.round(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function EditableField({ label, value, onChange, placeholder, valueClassName }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  valueClassName?: string;
}) {
  return (
    <div>
      <div className="text-micro uppercase tracking-wider text-muted-foreground">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "mt-0.5 w-full rounded-md border border-border bg-background px-2 py-1 text-caption text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20",
          valueClassName
        )}
      />
    </div>
  );
}

function EditableSelect({ label, value, onChange, options }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <div className="text-micro uppercase tracking-wider text-muted-foreground">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full rounded-md border border-border bg-background px-2 py-1 text-caption text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}


function CategoryGroup({ category, items, now, defaultOpen }: {
  category: Category;
  items: Deliverable[];
  now: Date;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const meta = CATEGORIES[category];
  const done = items.filter((d) => d.status === "complete").length;
  const blocked = items.filter((d) => d.status === "blocked").length;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30"
        style={{ borderLeft: `4px solid ${meta.color}` }}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body font-semibold text-foreground">{meta.label}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-micro tabular text-muted-foreground">
              {done}/{items.length}
            </span>
            {blocked > 0 && (
              <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-micro font-medium text-destructive">
                {blocked} blocked
              </span>
            )}
          </div>
        </div>
        <ChevronDown size={16} className={cn("text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="space-y-1.5 border-t border-border bg-muted/10 p-2">
          {items.length === 0 ? (
            <p className="py-6 text-center text-caption text-muted-foreground">No tasks match current filters.</p>
          ) : (
            items.map((d) => <TaskRow key={d.id} d={d} now={now} />)
          )}
        </div>
      )}
    </div>
  );
}

export function DeliverablesPanel() {
  const DELIVERABLES = useDeliverables();
  const now = useMemo(() => new Date(), []);
  const [cat, setCat] = useState<"all" | Category>("all");
  const [status, setStatus] = useState<"all" | Status>("all");
  const [due, setDue] = useState<DueBucket>("all");
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [query, setQuery] = useState("");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [addOpen, setAddOpen] = useState(false);
  const { isAdmin, sheetUrl } = useAuth();
  const qc = useQueryClient();
  const [rebucketing, setRebucketing] = useState(false);

  const migrateCategories = useCallback(async () => {
    setRebucketing(true);
    try {
      const dryRes = await fetch("/api/admin/migrate-categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dryRun: true }),
        credentials: "include",
      });
      if (!dryRes.ok) {
        alert(`Migrate failed: ${await dryRes.text()}`);
        return;
      }
      const preview = await dryRes.json();
      if (preview.wouldMigrate === 0) {
        alert("No legacy 'training' rows found. Sheet is already clean.");
        return;
      }
      const sample = (preview.sample ?? [])
        .slice(0, 12)
        .map((r: { id: string; name: string }) => `  #${r.id} ${r.name}`)
        .join("\n");
      const more = preview.wouldMigrate > 12 ? `\n  …and ${preview.wouldMigrate - 12} more.` : "";
      const ok = confirm(
        `Merge "Training & Enablement" into "Enablement & Training"?\n\n${preview.wouldMigrate} task(s) currently tagged 'training' will be patched to 'enablement' in the Sheet.\n\nFirst ${Math.min(12, preview.wouldMigrate)}:\n${sample}${more}`
      );
      if (!ok) return;
      const commitRes = await fetch("/api/admin/migrate-categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dryRun: false }),
        credentials: "include",
      });
      if (!commitRes.ok) {
        alert(`Commit failed: ${await commitRes.text()}`);
        return;
      }
      const result = await commitRes.json();
      alert(`Done. Migrated ${result.migrated} tasks.`);
      qc.invalidateQueries({ queryKey: ["deliverables"] });
    } finally {
      setRebucketing(false);
    }
  }, [qc]);

  const rebucketPhases = useCallback(async () => {
    setRebucketing(true);
    try {
      // Dry run first
      const dryRes = await fetch("/api/admin/reassign-phases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dryRun: true }),
        credentials: "include",
      });
      if (!dryRes.ok) {
        alert(`Re-bucket failed: ${await dryRes.text()}`);
        return;
      }
      const preview = await dryRes.json();
      const sample = (preview.sample ?? [])
        .slice(0, 12)
        .map((c: { id: string; name: string; from: string; to: string }) => `  #${c.id} ${c.name}: ${c.from || "—"} → ${c.to}`)
        .join("\n");
      const more = preview.wouldChange > 12 ? `\n  …and ${preview.wouldChange - 12} more.` : "";
      const ok = confirm(
        `Re-bucket ${preview.wouldChange} of ${preview.total} tasks based on the new phase boundaries?\n\nFirst ${Math.min(12, preview.wouldChange)} changes:\n${sample}${more}\n\nThis writes to the sheet and logs an activity entry on each task.`
      );
      if (!ok) return;
      const commitRes = await fetch("/api/admin/reassign-phases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dryRun: false }),
        credentials: "include",
      });
      if (!commitRes.ok) {
        alert(`Commit failed: ${await commitRes.text()}`);
        return;
      }
      const result = await commitRes.json();
      alert(`Done. Re-bucketed ${result.changed} tasks.`);
      qc.invalidateQueries({ queryKey: ["deliverables"] });
    } finally {
      setRebucketing(false);
    }
  }, [qc]);

  // Consume any pending filter from Overview click-through (owner / phase / category)
  useEffect(() => {
    const f = consumeTaskFilter();
    if (f?.owner) setOwnerFilter(f.owner);
    if (f?.category) setCat(f.category as Category);
  }, []);

  const owners = useMemo(() => {
    const set = new Set<string>();
    DELIVERABLES.forEach((d) => {
      const primary = primaryOwner(d.owner);
      if (primary) set.add(primary);
    });
    return Array.from(set).sort();
  }, [DELIVERABLES]);

  const filtered = useMemo(() => {
    return DELIVERABLES.filter((d) => {
      if (cat !== "all" && d.category !== cat) return false;
      if (status !== "all" && d.status !== status) return false;
      if (due !== "all" && bucketFor(d.due, now) !== due) return false;
      if (onlyFlagged && !d.flag) return false;
      if (ownerFilter !== "all") {
        const f = ownerFilter.toLowerCase();
        if (!d.owner.toLowerCase().includes(f)) return false;
      }
      if (query) {
        const q = query.toLowerCase();
        const hay = `${d.name} ${d.owner} ${d.notes ?? ""} ${d.writerSupport ?? ""} ${d.dependencies ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => dueSortKey(a.due, now) - dueSortKey(b.due, now));
  }, [DELIVERABLES, cat, status, due, onlyFlagged, query, ownerFilter, now]);

  const groups = useMemo(() => {
    const map = new Map<Category, Deliverable[]>();
    filtered.forEach((d) => {
      if (!map.has(d.category)) map.set(d.category, []);
      map.get(d.category)!.push(d);
    });
    // Return in CATEGORIES order
    return (Object.keys(CATEGORIES) as Category[])
      .filter((c) => map.has(c))
      .map((c) => ({ category: c, items: map.get(c)! }));
  }, [filtered]);

  const overdueCount = useMemo(
    () => DELIVERABLES.filter((d) => dueSortKey(d.due, now) < 0 && d.status !== "complete").length,
    [DELIVERABLES, now]
  );

  // Default only the first (non-complete) category open
  const firstOpenCat = groups.find((g) => g.items.some((i) => i.status !== "complete"))?.category ?? groups[0]?.category;

  // Stat counts (across all deliverables, not the filtered view — leadership
  // wants the absolute launch picture, not a slice).
  const stats = useMemo(() => {
    let active = 0, overdue = 0, blocked = 0, completed = 0;
    for (const d of DELIVERABLES) {
      if (d.status === "complete") completed++;
      else if (d.status === "blocked") blocked++;
      else if (d.status === "in_progress") active++;
      if (d.status !== "complete" && dueSortKey(d.due, now) < 0) overdue++;
    }
    return { active, overdue, blocked, completed };
  }, [DELIVERABLES, now]);

  return (
    <div className="space-y-4">
      {/* Stat strip — launch-wide pulse at a glance */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label="Active tasks"
          value={stats.active}
          Icon={CircleDot}
          iconClass="text-sky-600"
          iconBg="bg-sky-50"
        />
        <StatCard
          label="Overdue"
          value={stats.overdue}
          Icon={AlertCircle}
          iconClass="text-destructive"
          iconBg="bg-destructive/10"
          valueClass={stats.overdue > 0 ? "text-destructive" : undefined}
        />
        <StatCard
          label="Blocked"
          value={stats.blocked}
          Icon={Ban}
          iconClass="text-rose-600"
          iconBg="bg-rose-50"
        />
        <StatCard
          label="Completed"
          value={stats.completed}
          Icon={CheckCircle2}
          iconClass="text-emerald-600"
          iconBg="bg-emerald-50"
          footer={DELIVERABLES.length > 0 ? `${Math.round((stats.completed / DELIVERABLES.length) * 100)}% of ${DELIVERABLES.length}` : undefined}
        />
      </div>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CardTitle>Tasks · {filtered.length}</CardTitle>
              {overdueCount > 0 && (
                <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-micro font-semibold text-destructive">
                  {overdueCount} overdue
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search size={13} className="pointer-events-none absolute left-2.5 top-2.5 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className="h-8 w-44 rounded-md border border-input bg-card pl-7 pr-3 text-caption placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                />
              </div>
              <div className="relative">
                <Users size={13} className="pointer-events-none absolute left-2 top-2.5 text-muted-foreground" />
                <select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  className="h-8 rounded-md border border-input bg-card pl-7 pr-2 text-caption text-foreground focus:border-primary focus:outline-none"
                >
                  <option value="all">All people</option>
                  {owners.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <select value={cat} onChange={(e) => setCat(e.target.value as typeof cat)} className="h-8 rounded-md border border-input bg-card px-2 text-caption focus:border-primary focus:outline-none">
                <option value="all">All categories</option>
                {(Object.keys(CATEGORIES) as Category[]).map((k) => (
                  <option key={k} value={k}>{CATEGORIES[k].label}</option>
                ))}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="h-8 rounded-md border border-input bg-card px-2 text-caption focus:border-primary focus:outline-none">
                <option value="all">All statuses</option>
                {(Object.keys(STATUS_MAP) as Status[]).map((k) => (
                  <option key={k} value={k}>{STATUS_MAP[k].label}</option>
                ))}
              </select>
              <select value={due} onChange={(e) => setDue(e.target.value as DueBucket)} className="h-8 rounded-md border border-input bg-card px-2 text-caption focus:border-primary focus:outline-none">
                {DUE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setOnlyFlagged((v) => !v)}
                className={cn("inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-caption font-medium",
                  onlyFlagged ? "border-destructive/40 bg-destructive/10 text-destructive" : "border-input bg-card text-muted-foreground hover:text-foreground"
                )}
              >
                <Flag size={12} /> Flagged
              </button>
              {sheetUrl && (
                <a
                  href={sheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open the Deliverables Tracker Google Sheet in a new tab. Cell edits sync to the dashboard within ~30s — but don't rename, reorder, or delete columns."
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-card px-2.5 text-caption font-medium text-muted-foreground hover:border-primary/40 hover:text-primary"
                >
                  <ExternalLink size={12} /> Open in Sheet
                </a>
              )}
              {isAdmin && (
                <>
                  <button
                    type="button"
                    onClick={migrateCategories}
                    disabled={rebucketing}
                    title="Merge legacy 'training' tasks into 'enablement'. Idempotent — safe to run multiple times."
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-caption font-medium text-muted-foreground hover:border-primary/40 hover:text-primary disabled:opacity-50"
                  >
                    Merge categories
                  </button>
                  <button
                    type="button"
                    onClick={rebucketPhases}
                    disabled={rebucketing}
                    title="Re-bucket every task into the new phase windows based on its due date. Admin-only."
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-caption font-medium text-muted-foreground hover:border-primary/40 hover:text-primary disabled:opacity-50"
                  >
                    {rebucketing ? "Re-bucketing…" : "Re-bucket phases"}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-caption font-medium text-white hover:bg-primary/90"
              >
                <Plus size={13} /> Add
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {groups.length === 0 ? (
            <div className="py-12 text-center text-caption text-muted-foreground">No tasks match these filters.</div>
          ) : (
            groups.map((g) => (
              <CategoryGroup
                key={g.category}
                category={g.category}
                items={g.items}
                now={now}
                defaultOpen={g.category === firstOpenCat}
              />
            ))
          )}
          <div className="pt-2 text-center text-micro text-muted-foreground">
            <CalendarClock size={10} className="mr-1 inline" /> Sorted by soonest due date within each category.
          </div>
        </CardContent>
      </Card>
      <CreateDeliverableModal open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}
